/**
 * Anzeigekomponente fuer Artikelbilder aus JTL - nutzt ausschliesslich den bereits vorhandenen
 * Endpunkt `GET /api/jtl/articles/:kArtikel/image?size=thumb|card`
 * (`backend/src/http/routes/jtl.ts:159-195`, `backend/src/jtl/bilder.ts`). Der Einbau in die
 * Tabellen ist inzwischen geschehen (Auftrag 2 und 4).
 *
 * ================= Auftrag 5: authentifizierter Abruf statt `<img src>` =================
 *
 * `463662a` (Auftrag 1/2/4) schrieb die API-URL direkt in `src` - lief in Produktion, weil das
 * Frontend AUSSCHLIESSLICH per `Authorization: Bearer <token>` authentifiziert
 * (`api/basis.ts:standardKopfzeilen`) und ein `<img src>` diesen Header nicht mitschicken kann: das
 * Backend liest den Token ausschliesslich daraus (`backend/src/http/middleware/authenticate.ts:50`,
 * keine Cookie-Auswertung im Backend) - jede Bildanfrage lief also in einen 401, sichtbar nur als
 * grauer Platzhalter. Das Vorbild `web_wms` (Django, Session-Cookie) verdeckte diesen Unterschied.
 *
 * Der Fix: `api/basis.ts#holeBinaerMitStatus` (derselbe Header-Aufbau wie jede andere API-Anfrage) laedt das
 * Bild als `Blob`, `useAuthentifiziertesBild` unten macht daraus per `URL.createObjectURL` eine
 * Object-URL - NUR die steht noch in `src`. `loading="lazy"` wirkt nur auf `src`-Ladung durch den
 * Browser und ist damit wirkungslos, sobald selbst geladen wird - Ersatz ist `useSichtbar`
 * (`IntersectionObserver`, Vorlauf ueber `rootMargin`) fuer das Thumbnail; das Grossbild im Overlay
 * laedt weiterhin sofort (`aktiv` fest `true`, sinngemaess das bisherige `loading="eager"`).
 *
 * ================= Dialog-/Overlay-Muster im Projekt =================
 *
 * Geprueft: `Drawer/DocsTab/UeberschreibDialog.tsx`, `Users/PasswortDialog.tsx` (beide `role="dialog"
 * aria-modal="true"`, `createPortal` nach `document.body`, Hintergrundklick schliesst ueber
 * `event.target === event.currentTarget` - aber KEIN Escape, KEINE Fokusverwaltung) und
 * `Board/KontextMenue.tsx` (Escape UND Anfangsfokus auf den ersten Knopf - aber `role="menu"`, kein
 * Portal, KEINE Fokus-Rueckgabe an das ausloesende Element). Kein einzelnes Muster deckt alle hier
 * verlangten Punkte (Escape, Hintergrundklick, Schliessen-Knopf, Fokus rein UND zurueck,
 * `role="dialog"`/`aria-modal`) ab - dieses Overlay uebernimmt Portal und Hintergrundklick von den
 * Dialogen, Escape/Anfangsfokus sinngemaess von `KontextMenue`, und ergaenzt die Fokus-Rueckgabe neu.
 *
 * NACHBESSERUNG (Pruefung 5bdfb6c, Punkt 4/9): Farbe/Blur/Schatten der Ueberlagerung sind seither
 * KEINE eigene Nachbildung mehr, sondern `.board-editor-overlay`/`.board-editor-dialog`
 * (`bestand.css:246-259`), genau wie `PasswortDialog.tsx:66` - siehe Kommentar an `ArtikelbildOverlay`.
 * Und weil `aria-modal="true"` ein Versprechen ist, das die beiden oben geprueften Dialoge NICHT
 * einloesen (Fokus wandert dort mit Tab aus ihnen heraus), haelt diese Komponente den Fokus jetzt
 * WIRKLICH im Dialog - der einzige Punkt, an dem sie bewusst ueber ihre Vorbilder hinausgeht.
 *
 * ================= NACHBESSERUNG (Pruefung fc68056): vier Punkte an Auftrag 5 =================
 *
 * 1. (im Testcode) Der Wettlauf-Test spuelte bislang nur zwei Mikrotasks aus - zu kurz fuer die
 *    Kette `fetch -> await .blob() -> .then()`. `test/komponenten/artikelbild.test.tsx` wartet jetzt
 *    ueber einen echten Makrotask (`setTimeout`).
 * 2. `useSichtbar` beobachtete bislang gegen `root: null` (Viewport) - siehe
 *    `naechsterScrollbarerVorfahre` oben fuer den Grund und die Herleitung.
 * 3. `onError` fehlte seit dem Umbau auf `fetch`/Object-URL ersatzlos - `holeBinaerMitStatus` prueft
 *    nur den HTTP-Status, nicht den Inhaltstyp. `meldeAnzeigefehler` (`useAuthentifiziertesBild` unten)
 *    schaltet bei einem nicht dekodierbaren Bild auf denselben Fehlschlag-Zustand wie ein 404.
 * 4. `alt`/`aria-hidden` haengen jetzt an `fehlgeschlagen`, nicht mehr an `zeigtEchtesBild` - der
 *    Artikelname steht damit sofort, waehrend die Anfrage noch laeuft (siehe `Artikelbild` unten).
 *
 * ========== NACHBESSERUNG: doppelte 404-Zeilen bei GLEICHZEITIGEM Mount desselben Artikels ==========
 *
 * In Produktion beobachtet: dieselbe Artikel-ID erzeugte manchmal ZWEI 404-Konsolenzeilen fuer
 * exakt dieselbe URL innerhalb EINES Seitenaufbaus (z. B. dieselbe Position gleichzeitig in
 * Positionsliste UND EK-Kalkulation). Ursache: `bekanntBildlos` (oben) wird erst NACH Abschluss
 * einer Anfrage befuellt - mounten zwei Instanzen mit demselben `pfad` GLEICHZEITIG, sehen beide
 * Effekt-Durchlaeufe den noch leeren Cache und feuern je einen eigenen `fetch`. Der sitzungsweite
 * Cache verhindert also nur SEQUENZIELLE Wiederholungen, keine GLEICHZEITIGEN. Behoben ueber
 * `laufendeAnfragen`/`holeMitDeduplizierung` (unten, jeweils eigener Kopfkommentar) - eine
 * zusaetzliche Ebene NEBEN `bekanntBildlos`, keine Ablösung davon.
 *
 * ========== AUFTRAG "hatBild vorab mitgeben" (2026-08-27): die ERSTE Anfrage selbst entfaellt ==========
 *
 * `bekanntBildlos` und `laufendeAnfragen` verhindern WIEDERHOLTE bzw. GLEICHZEITIGE Anfragen fuer
 * denselben Artikel - die ERSTE Anfrage fuer einen bildlosen Artikel loesten beide bislang trotzdem
 * aus, sichtbar als eine 404-Zeile in der Konsole bei JEDEM Seitenaufbau, einmal je bildlosem
 * Artikel. Die drei Backend-Endpunkte, die Artikeltabellen/-suche speisen (`GET
 * /api/jtl/orders(/:k)`, `GET /api/jtl/articles`), liefern seither `hatBild: boolean` MIT - aus
 * DENSELBEN Daten, die sie ohnehin schon lesen (`backend/src/jtl/bilder.ts#leseHatBildFuerArtikel`,
 * EINE Batch-Abfrage, kein N+1). Die neue Prop `hatBild` unten reicht das durch: `false` schaltet
 * den Netzaufruf VORAB ab, `true`/`undefined` (Rueckwaertskompatibilitaet fuer noch nicht
 * verdrahtete Aufrufstellen) lassen das bisherige Verhalten unveraendert.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { createPortal } from "react-dom";
import { holeBinaerMitStatus } from "../api/basis.ts";
import { artikelbildPlatzhalter, artikelbildUrl } from "../lib/artikelbild.ts";
import { Ikone } from "./Ikone.tsx";

/** Vorlauf des `IntersectionObserver` (Auftrag 5) - damit beim Scrollen kein Bild sichtbar nachklappt. */
const SICHTBARKEITS_VORLAUF = "300px";

/**
 * Sitzungsweiter Cache "bekannt bildlos" - Modul-Zustand (kein `localStorage`), zurueckgesetzt bei
 * einem vollstaendigen Seitenneuladen. Derselbe `pfad` wie beim `fetch` selbst
 * (`/api/jtl/articles/:kArtikel/image?size=thumb|card`, siehe `lib/artikelbild.ts#artikelbildUrl`)
 * dient als Schluessel - er codiert `kArtikel` UND `size` bereits eindeutig, eine gesonderte
 * Zusammensetzung waere nur eine zweite Kodierung derselben zwei Werte.
 *
 * Grund (siehe Auftrag): dieselbe Kachel mountet in fuenf Artikeltabellen und zwei Trefferlisten der
 * Artikelsuche wiederholt neu (Scrollen, Tab-Wechsel, Drawer auf/zu) - ohne diesen Cache erzeugte
 * JEDER erneute Mount fuer einen Artikel ohne Bild in JTL einen erneuten `fetch` und damit eine
 * weitere 404-Zeile in der DevTools-Konsole (die protokolliert jede Netzwerkantwort ab 4xx
 * automatisch, unabhaengig von `try`/`catch` - der Netzaufruf selbst muss also entfallen). Die
 * 404-Antwort bleibt unveraendert bestehen (siehe `api/basis.ts#holeBinaerMitStatus`), nur die
 * WIEDERHOLUNG fuer denselben Artikel entfaellt.
 *
 * Traegt AUSSCHLIESSLICH einen ECHTEN 404 ein (siehe `useAuthentifiziertesBild` unten) - ein
 * 401/500/Netzfehler kann sich spaeter erledigen (Sitzung erneuert, Netz wieder da) und muss beim
 * naechsten Sichtbarwerden erneut versucht werden.
 */
const bekanntBildlos = new Set<string>();

/**
 * NUR fuer Tests: setzt den Cache zwischen Testfaellen zurueck. Ohne diesen Aufruf in
 * `beforeEach`/`afterEach` leckt ein in einem Testfall eingetragener 404 in JEDEN folgenden Testfall
 * DERSELBEN Datei (Vitest laedt das Modul nur einmal je Testdatei) - siehe
 * `test/komponenten/artikelbild.test.tsx`.
 */
export function __zuruecksetzenFuerTests(): void {
  bekanntBildlos.clear();
  laufendeAnfragen.clear();
}

/**
 * In-Flight-Deduplizierung fuer GLEICHZEITIGE Anfragen desselben `pfad` - eine Luecke, die
 * `bekanntBildlos` oben NICHT schliesst: der dortige Cache wird erst NACH Abschluss einer Anfrage
 * befuellt (`holeMitDeduplizierung` unten), verhindert also nur WIEDERHOLTE Anfragen NACH einem
 * abgeschlossenen Ladevorgang (Unmount -> spaeterer Remount). Mounten zwei `Artikelbild`-
 * Komponenten mit DERSELBEN Kennung (also demselben `pfad`) GLEICHZEITIG - z. B. dieselbe Position
 * in zwei Tabellen derselben Seite (Positionsliste + EK-Kalkulation) oder eine Trefferliste neben
 * einer bereits offenen Tabelle mit demselben Artikel -, laufen beide Effekt-Instanzen VOR der
 * ersten Antwort los und sehen beide den noch leeren `bekanntBildlos`-Cache: ohne diese Map feuert
 * jede ihren EIGENEN `fetch`, sichtbar in Produktion als zwei identische 404-Zeilen fuer dieselbe
 * URL im selben Seitenaufbau.
 *
 * Haelt darum den LAUFENDEN Promise je `pfad` fest, ab dem Moment, in dem die erste Anfrage
 * losgeht - eine zweite, gleichzeitige Anfrage fuer denselben `pfad` haengt sich an GENAU DIESEN
 * Promise an (`holeMitDeduplizierung` unten), statt `holeBinaerMitStatus` ein zweites Mal
 * aufzurufen. Der Eintrag wird ENTFERNT, sobald der Promise abgeschlossen ist (Erfolg wie
 * Fehlschlag gleichermassen - `holeBinaerMitStatus` wirft ohnehin nie, siehe dort) - sonst bliebe
 * ein SPAETERER, unabhaengiger Ladevorgang fuer denselben Artikel (z. B. nach einem echten
 * `bekanntBildlos`-Reset ueber `__zuruecksetzenFuerTests` oder in einer neuen Sitzung) fuer immer
 * an einem laengst aufgeloesten Promise haengen.
 */
const laufendeAnfragen = new Map<string, ReturnType<typeof holeBinaerMitStatus>>();

/**
 * Holt `pfad` hoechstens EINMAL gleichzeitig (siehe `laufendeAnfragen` oben) und traegt einen
 * ECHTEN 404 GENAU EINMAL in `bekanntBildlos` ein - absichtlich HIER, am geteilten Promise selbst,
 * NICHT im `.then()` jeder einzelnen aufrufenden Komponente: mehrere Abonnenten desselben Promise
 * riefen `bekanntBildlos.add(pfad)` sonst mehrfach auf (waere idempotent und damit harmlos, `Set.add`
 * ueberschreibt sich selbst - aber unnoetig und ein Umweg um die eigentliche Garantie). Wichtiger:
 * der Cache-Eintrag muss unabhaengig davon passieren, ob eine EINZELNE Komponente ihre Antwort
 * anschliessend als `veraltet` verwirft (`useAuthentifiziertesBild` unten, geteilter Promise UND
 * `veraltet` sind zwei unabhaengige Mechanismen) - genau wie bisher bei der einzelnen Anfrage vor
 * dieser Aenderung.
 */
function holeMitDeduplizierung(pfad: string): ReturnType<typeof holeBinaerMitStatus> {
  let anfrage = laufendeAnfragen.get(pfad);
  if (!anfrage) {
    anfrage = holeBinaerMitStatus(pfad).then((ergebnis) => {
      if (ergebnis.status === 404) bekanntBildlos.add(pfad);
      return ergebnis;
    });
    laufendeAnfragen.set(pfad, anfrage);
    void anfrage.finally(() => {
      laufendeAnfragen.delete(pfad);
    });
  }
  return anfrage;
}

/** `overflow-y`/`overflow-x`-Werte, die einen Knoten selbst scrollen lassen (Kandidat fuer `root`). */
const SCROLLBARE_OVERFLOW_WERTE = new Set(["auto", "scroll", "overlay"]);

/**
 * Der naechste scrollbare Vorfahre von `knoten` (NACHBESSERUNG, Pruefung fc68056, Punkt 2): `root:
 * null` (bisher) beobachtet gegen den VIEWPORT - alle fuenf Einbaustellen
 * (`Drawer/MainTab/Positionen.tsx`, `Drawer/CalcTab/PositionsTabelle.tsx` ueber `.drawer-body`,
 * `NewOrder/PositionsEditor.tsx` ueber `.board-editor-body`, `NewOrder/TeillieferungDialog.tsx`/
 * `SammellieferungDialog.tsx` ueber `.partial-delivery-body`/`.partial-table-wrap`) scrollen aber in
 * einem EIGENEN Container, nie im Viewport selbst. `rootMargin` (der 300-px-Vorlauf oben) weitet nur
 * das Rechteck der ROOT - liegt root beim Viewport, bleibt das Clip-Rechteck des dazwischenliegenden
 * Containers unveraendert eng, und der Vorlauf verpufft: Bilder klappen beim Scrollen sichtbar nach.
 *
 * `document.documentElement`/`document.body` zaehlen bewusst NICHT als Treffer - `root: null`
 * bedeutet fuer den `IntersectionObserver` ohnehin "der Viewport", dasselbe Ergebnis, ohne dass eine
 * spaeter ausgetauschte `<html>`/`<body>`-Referenz haengen bleibt.
 */
function naechsterScrollbarerVorfahre(knoten: Element): Element | null {
  let vorfahre = knoten.parentElement;
  while (vorfahre && vorfahre !== document.documentElement && vorfahre !== document.body) {
    const stil = getComputedStyle(vorfahre);
    if (SCROLLBARE_OVERFLOW_WERTE.has(stil.overflowY) || SCROLLBARE_OVERFLOW_WERTE.has(stil.overflowX)) {
      return vorfahre;
    }
    vorfahre = vorfahre.parentElement;
  }
  return null;
}

/**
 * `true`, sobald das beobachtete `<img>` (einmal) in Sichtweite war - danach bleibt es dabei (kein
 * Abschalten beim Wegscrollen, das verlangt der Auftrag nicht und wuerde nur zusaetzliche
 * Netzaufrufe erzeugen). Ersatz fuer das wirkungslos gewordene `loading="lazy"` (Kopfkommentar
 * oben). `setzeKnoten` gehoert als `ref`-Callback ans `<img>`.
 *
 * BEWUSST ein Callback-Ref (Zustand), KEIN `useRef`: `Artikelbild` ist bei einer frisch angelegten
 * Position bereits VOR der Artikelauswahl gemountet (Positionsliste/-editor legen die Zeile leer
 * an) - beim Wechsel von "keine Kennung" zu "gueltige Kennung" bleibt es DIESELBE
 * Komponenteninstanz, nur der fruehe `if (!url) return ...`-Zweig oben entfaellt. Ein `useRef`
 * aendert sich dabei nicht (dieselbe Objektidentitaet), ein Effekt mit `[ref]` in den Abhaengigkeiten
 * liefe darum KEIN zweites Mal, obwohl `ref.current` erst jetzt ein echtes `<img>` traegt - das
 * Thumbnail bliebe für immer beim Platzhalter stehen. Ein Callback-Ref loest dagegen bei JEDER
 * Aenderung der DOM-Knoten-Identitaet (auch vom ersten Erscheinen) einen State-Update und damit
 * einen Effekt-Durchlauf aus.
 *
 * jsdom kennt `IntersectionObserver` nicht - der Ersatz dafuer steht im Testaufbau
 * (`test/setup.ts`), nicht hier: eine fehlende Weltraum-API in einer Testumgebung ist keine
 * Rechtfertigung fuer eine Verrenkung im Produktionscode. Fehlt die API dennoch (sehr alte
 * Browser), wird sofort geladen statt nie - eine fehlende API darf ein Bild nicht dauerhaft
 * verstecken.
 */
function useSichtbar(): { setzeKnoten: (knoten: Element | null) => void; sichtbar: boolean } {
  const [knoten, setKnoten] = useState<Element | null>(null);
  const [sichtbar, setSichtbar] = useState(false);

  useEffect(() => {
    if (sichtbar || !knoten) return;
    if (typeof IntersectionObserver === "undefined") {
      setSichtbar(true);
      return;
    }
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((eintrag) => eintrag.isIntersecting)) {
          setSichtbar(true);
          beobachter.disconnect();
        }
      },
      // `root`: der naechste scrollbare Vorfahre statt `null` (Viewport) - siehe Kopfkommentar von
      // `naechsterScrollbarerVorfahre` (NACHBESSERUNG, Pruefung fc68056, Punkt 2). Faellt auf `null`
      // zurueck, wenn keiner gefunden wird - dann gilt weiterhin der Viewport.
      { root: naechsterScrollbarerVorfahre(knoten), rootMargin: SICHTBARKEITS_VORLAUF },
    );
    beobachter.observe(knoten);
    return () => beobachter.disconnect();
  }, [knoten, sichtbar]);

  return { setzeKnoten: setKnoten, sichtbar };
}

/**
 * Laedt `pfad` (Auftrag 5) ueber `api/basis.ts#holeBinaerMitStatus` als Object-URL, sobald `aktiv`
 * gilt - fuer das Thumbnail ist das "in Sichtweite" (`useSichtbar` oben), fuer das Grossbild-Overlay
 * immer `true` (sofortiges Laden, das bisherige `loading="eager"`).
 *
 * Zwei Pflichten aus dem Auftrag, beide hier gebuendelt statt am Aufrufer:
 * - Objekt-URLs werden wieder freigegeben (`URL.revokeObjectURL`) - sowohl beim Wechsel von `pfad`
 *   (die vorherige Object-URL wird UNMITTELBAR vor dem naechsten Ladevorgang freigegeben, `ref`
 *   unten haelt sie dafuer fest) als auch beim endgueltigen Verschwinden der Komponente (eigener
 *   Effekt mit leerem Abhaengigkeitsfeld - der laeuft NUR beim Unmount, nicht bei jedem
 *   `pfad`-Wechsel, den deckt der erste Effekt schon ab).
 * - Kein Wettlauf: `veraltet` (ein Gueltigkeitsmerkmal, gesetzt in der Aufraeumfunktion des
 *   Effekts) verhindert, dass eine spaet eintreffende Antwort nach einem zwischenzeitlichen
 *   `pfad`-Wechsel noch angewendet wird - weder als Bild noch als Fehlerzustand.
 *
 * Sitzungsweiter 404-Cache (`bekanntBildlos` oben, Kopfkommentar der Datei): ist `pfad` bereits als
 * bildlos bekannt, wird gar nicht erst geladen - derselbe Anzeigezustand (`fehlgeschlagen = true`,
 * Platzhalter, kein Klickweg) entsteht sofort, ohne Netzaufruf und damit ohne erneute 404-Zeile in
 * der Konsole. Ein ECHTER 404 (nicht 401/500/Netzfehler) traegt die Kombination in den Cache ein -
 * das passiert innerhalb von `holeMitDeduplizierung` (Kopfkommentar dort), nicht hier.
 *
 * In-Flight-Deduplizierung (`laufendeAnfragen`/`holeMitDeduplizierung` oben, Kopfkommentar dort):
 * `holeBinaerMitStatus` selbst wird nie direkt aufgerufen, sondern nur ueber `holeMitDeduplizierung`
 * - mounten zwei Komponenten mit demselben `pfad` gleichzeitig, teilen sie sich EINEN Promise und
 * damit EINEN Netzaufruf. `veraltet` bleibt davon unberuehrt: es gilt weiterhin ausschliesslich
 * dieser einen Effekt-Instanz, unabhaengig davon, ob sie den geteilten Promise selbst ausgeloest
 * oder sich nur angehaengt hat.
 *
 * `hatBild` (Auftrag "hatBild vorab mitgeben"): `false` wirkt wie ein VORAB bekannter 404 - der
 * `if (pfad && aktiv)`-Zweig unten schaltet dann OHNE jeden Netzaufruf (auch keinen ueber
 * `holeMitDeduplizierung`) sofort auf `fehlgeschlagen`. Bewusst NICHT in `bekanntBildlos`
 * eingetragen: dieser Cache markiert einen BESTAETIGTEN 404 dieser Sitzung, `hatBild={false}` ist
 * dagegen eine VORAB-Auskunft der aufrufenden Liste - beide Mechanismen bleiben getrennt, ein
 * spaeterer Aufruf mit `hatBild={true}`/`undefined` fuer DIESELBE Kennung (z. B. nachdem JTL ein
 * Bild bekommen hat) darf also weiterhin normal nachfragen.
 *
 * EXPORT (Auftrag Task 4 "Produktionsbilder-Lightbox"): zweiter Verwender ist
 * `Drawer/DocsTab/ProduktionsbildLightbox.tsx` - die Produktionsbild-Vorschau laeuft ueber
 * DENSELBEN authentifizierten Weg (`holeBinaerMitStatus` + Object-URL), nicht ueber ein
 * `<img src="/api/...">`, das den Bearer-Header nicht mitschicken kann (siehe Kopfkommentar der
 * Datei). Nur die Sichtbarkeit hat sich geaendert: `Artikelbild`/`ArtikelbildOverlay` oben rufen die
 * Funktion unveraendert auf.
 */
export function useAuthentifiziertesBild(
  pfad: string | null,
  aktiv: boolean,
  hatBild: boolean | undefined,
): { objectUrl: string | null; fehlgeschlagen: boolean; meldeAnzeigefehler: () => void } {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let veraltet = false;

    setFehlgeschlagen(false);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);

    if (pfad && aktiv) {
      if (hatBild === false) {
        // Vorab als bildlos bekannt (aus der Artikelliste/-suche, siehe Kopfkommentar oben) - kein
        // Netzaufruf, sofort derselbe Anzeigezustand wie nach einem bestaetigten 404.
        setFehlgeschlagen(true);
      } else if (bekanntBildlos.has(pfad)) {
        // Bereits als bildlos bekannt (frueherer echter 404 fuer dieselbe Kombination aus kArtikel
        // und Groesse, in DIESER Sitzung) - kein erneuter Netzaufruf, sofort derselbe
        // Anzeigezustand wie nach einem frischen 404.
        setFehlgeschlagen(true);
      } else {
        // `holeMitDeduplizierung` statt `holeBinaerMitStatus` direkt (Nachbesserung, gleichzeitige
        // Anfragen): traegt den Cache-Eintrag selbst schon EINMAL ein, unabhaengig von `veraltet`
        // dieser Instanz - siehe Kopfkommentar dort fuer den Grund (frueher stand dieselbe
        // Begruendung hier, jetzt zentral an der Stelle, die den Eintrag tatsaechlich vornimmt).
        // `veraltet` bleibt dennoch unveraendert: es gilt weiterhin ausschliesslich dem Setzen von
        // React-State DIESER Instanz, unabhaengig davon, ob sie den Promise selbst gestartet oder
        // sich nur an eine bereits laufende, geteilte Anfrage angehaengt hat.
        holeMitDeduplizierung(pfad).then(({ blob }) => {
          if (veraltet) return;
          if (!blob) {
            // 404 (kein Bild in JTL, der Normalfall) UND 401/500/Netzfehler fuehren gleichermassen
            // zum Platzhalter - NUR ein echter 404 ist sitzungsweit cachefaehig (siehe
            // Kopfkommentar `bekanntBildlos`): 401/500/Netzfehler koennen sich spaeter erledigen
            // (Sitzung erneuert, Netz wieder da) und muessen beim naechsten Sichtbarwerden erneut
            // versucht werden.
            setFehlgeschlagen(true);
            return;
          }
          const neu = URL.createObjectURL(blob);
          objectUrlRef.current = neu;
          setObjectUrl(neu);
        });
      }
    }

    return () => {
      veraltet = true;
    };
  }, [pfad, aktiv, hatBild]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  /**
   * Fuer `onError` am `<img>` (NACHBESSERUNG, Pruefung fc68056, Punkt 3): `holeBinaerMitStatus`
   * prueft nur `antwort.ok` (`api/basis.ts`), NICHT den Inhaltstyp - antwortet etwas Zwischengeschaltetes mit
   * Status 200 und HTML statt Bilddaten, entsteht trotzdem eine Object-URL, die der Browser nicht
   * dekodieren kann. Der Browser meldet das ausschliesslich ueber das `error`-Ereignis am `<img>`
   * selbst, NICHT ueber `fetch`/`.blob()` - hier das Netz dagegen: schaltet auf denselben
   * Fehlschlag-Zustand wie ein 404/401 (Platzhalter, kein Klickweg - siehe `Artikelbild` unten) und
   * gibt die kaputte Object-URL frei, die sonst bis zum naechsten Ladevorgang/Unmount haengen bliebe.
   */
  function meldeAnzeigefehler(): void {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
    setFehlgeschlagen(true);
  }

  return { objectUrl, fehlgeschlagen, meldeAnzeigefehler };
}

export interface ArtikelbildProps {
  /** Roh aus den Positionsdaten (z. B. `Order`/`Position.kArtikel`) - Pruefung passiert drinnen. */
  kArtikel: unknown;
  /** Fuer den `alt`-Text. */
  name?: unknown;
  /** Vorgabe `true`. `false` schaltet das Grossbild-Overlay ab - dann bleibt es ein `<img>`. */
  klickbar?: boolean;
  /**
   * Vorab bekannt aus der ohnehin geladenen Artikelliste/-suche (`hatBild` im Backend-DTO, Auftrag
   * "hatBild vorab mitgeben", 2026-08-27). `false` unterdrueckt die Bildanfrage VOLLSTAENDIG - kein
   * Netzaufruf, sofort derselbe Anzeigezustand wie nach einem bestaetigten 404 (Platzhalter, kein
   * Klickweg). `true`/`undefined` (Vorgabe, Rueckwaertskompatibilitaet fuer noch nicht verdrahtete
   * Aufrufstellen) lassen das bisherige Verhalten unveraendert: Anfrage stellen, ggf. ueber
   * `bekanntBildlos`/`laufendeAnfragen` gedeckelt (siehe Kopfkommentar der Datei).
   */
  hatBild?: boolean;
}

function altText(name: unknown): string {
  return typeof name === "string" && name.trim().length > 0 ? name : "Artikelbild";
}

export function Artikelbild({ kArtikel, name, klickbar = true, hatBild }: ArtikelbildProps): JSX.Element {
  const url = artikelbildUrl(kArtikel, "thumb");
  const beschriftung = altText(name);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [overlayOffen, setOverlayOffen] = useState(false);

  // Auftrag 5: `sichtbar` ersetzt das wirkungslos gewordene `loading="lazy"` - geladen wird erst,
  // wenn das `<img>` (an das `setzeKnoten` unten als `ref` haengt) in Sichtweite kommt.
  // `objectUrl`/`fehlgeschlagen` kommen aus `useAuthentifiziertesBild` - siehe dortigen
  // Kopfkommentar fuer Freigabe und Wettlaufschutz. Beide Aufrufe stehen VOR dem fruehen `return`
  // unten (Regel der Hooks: gleiche Reihenfolge bei jedem Rendern, unabhaengig von `url`).
  const { setzeKnoten, sichtbar } = useSichtbar();
  const { objectUrl, fehlgeschlagen, meldeAnzeigefehler } = useAuthentifiziertesBild(url, sichtbar, hatBild);

  function schliessenUndFokusZurueck(): void {
    setOverlayOffen(false);
    triggerRef.current?.focus();
  }

  if (!url) {
    // Ohne gueltige Kennung: sofort der Platzhalter, kein Netzaufruf. Er traegt keine eigene
    // Information (das Fehlen einer Kennung ist der Normalfall bei Freipositionen).
    return (
      <img
        className="artikelbild-thumb"
        src={artikelbildPlatzhalter()}
        alt=""
        aria-hidden="true"
        width={30}
        height={30}
      />
    );
  }

  // Echtes Bild nur, wenn `holeBinaerMitStatus()` tatsaechlich einen Blob lieferte. Vor Abschluss der
  // Anfrage (noch nicht sichtbar ODER Anfrage laeuft noch) UND nach einem Fehlschlag (404/401/
  // sonstiger Fehlstatus) zeigt das `<img>` gleichermassen den Platzhalter - der Unterschied ist
  // fuer den Betrachter ohne Belang, ein Ladezustand ist kein Fehler.
  const zeigtEchtesBild = objectUrl !== null;

  // NACHBESSERUNG (Pruefung fc68056, Punkt 4): `alt`/`aria-hidden` haengen bewusst NICHT mehr an
  // `zeigtEchtesBild` (das waere erst nach dem Blob wahr), sondern an `fehlgeschlagen` - der
  // Artikelname steht damit SOFORT, waehrend der Anfrage noch laeuft, nicht erst nach dem Laden. Nur
  // nach einem BESTAETIGTEN Fehlschlag (404/401/sonstiger Fehlstatus - der Platzhalter traegt dann
  // wirklich keine Information) faellt die Zeile wieder auf `alt=""`/`aria-hidden="true"` zurueck.
  const bild = (
    <img
      ref={setzeKnoten}
      className="artikelbild-thumb"
      src={zeigtEchtesBild ? objectUrl : artikelbildPlatzhalter()}
      alt={fehlgeschlagen ? "" : beschriftung}
      aria-hidden={fehlgeschlagen ? "true" : undefined}
      decoding="async"
      width={30}
      height={30}
      // NACHBESSERUNG (Pruefung fc68056, Punkt 3): nur am ECHTEN Bild verdrahtet - der eingebaute
      // `data:image/svg+xml`-Platzhalter kann der Browser immer dekodieren, ein `onError` dafuer
      // waere ohne Gegenstand. Siehe `meldeAnzeigefehler` (`useAuthentifiziertesBild` oben) fuer den
      // Grund: `holeBinaerMitStatus` prueft nur den HTTP-Status, nicht den Inhaltstyp der Antwort.
      onError={zeigtEchtesBild ? meldeAnzeigefehler : undefined}
    />
  );

  // NACHBESSERUNG (Pruefung 5bdfb6c, Punkt 1): nach einem thumb-404 - dem Normalfall "dieser Artikel
  // hat in JTL kein Bild" - darf kein Klickweg mehr bestehen. `size=card` traegt dieselbe Kennung,
  // also derselbe 404 - ein Klick oeffnete bislang ein Overlay, das nur das Bruchbild-Symbol des
  // Browsers zeigen konnte. Ohne gueltige Kennung (oben) wie nach einem Fehlschlag (hier) bleibt es
  // deshalb ein einfaches `<img>`.
  if (!klickbar || fehlgeschlagen) return bild;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="artikelbild-thumb-btn"
        aria-label={`${beschriftung} groß anzeigen`}
        onClick={() => setOverlayOffen(true)}
      >
        {bild}
      </button>
      {overlayOffen ? (
        // `url` ist hier ungleich `null` (siehe `if (!url) return ...` oben) - `kArtikel` also
        // bereits als gueltige Ganzzahl >= 1 geprueft. Das Overlay bekommt deshalb die GEPRUEFTE
        // Kennung (`number`) statt `unknown` (Nachbesserung, Pruefung 5bdfb6c, Punkt 7) und muss sie
        // innen nicht ein zweites Mal pruefen.
        <ArtikelbildOverlay
          kArtikel={kArtikel as number}
          beschriftung={beschriftung}
          onSchliessen={schliessenUndFokusZurueck}
        />
      ) : null}
    </>
  );
}

interface ArtikelbildOverlayProps {
  /** Bereits geprueft (siehe Aufrufer oben) - Ganzzahl >= 1. */
  kArtikel: number;
  beschriftung: string;
  onSchliessen: () => void;
}

/** Grossbild (`size=card`, 400 px) - eigene, kleine Komponente in derselben Datei wie `Artikelbild`. */
function ArtikelbildOverlay({ kArtikel, beschriftung, onSchliessen }: ArtikelbildOverlayProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const schliessenKnopfRef = useRef<HTMLButtonElement>(null);
  // `kArtikel` ist laut Vertrag der Props (siehe dort) bereits eine gepruefte Ganzzahl >= 1 -
  // `artikelbildUrl` kann dafuer nicht `null` liefern. Kein Ternaer mehr "nur fuer den Typ"
  // (Nachbesserung, Pruefung 5bdfb6c, Punkt 7).
  const grossUrl = artikelbildUrl(kArtikel, "card")!;

  // NACHBESSERUNG (Pruefung 5bdfb6c, Punkt 1): `bVorschauBild` (thumb) und `bBild` (card) sind zwei
  // verschiedene JTL-Spalten - dass die Kachel bis hierher ueberhaupt anklickbar war, sagt ueber
  // `size=card` nichts aus. Dieselbe Platzhalter-Umschaltung wie am Thumbnail, DAMIT auch ein erst
  // hier auftretender 404 nicht als Bruchbild-Symbol des Browsers endet.
  //
  // Auftrag 5: `aktiv` fest `true` - das Grossbild soll sofort laden, sinngemaess das bisherige
  // `loading="eager"` (ein `IntersectionObserver` waere hier sinnlos, das Overlay ist beim Rendern
  // bereits sichtbar). `fehlgeschlagen` wird hier nicht gesondert gebraucht: solange `grossObjectUrl`
  // `null` ist - ob noch ladend oder endgueltig fehlgeschlagen (404/401/Fehlstatus) -, zeigt das
  // `<img>` unten gleichermassen den Platzhalter (derselbe Ladezustand-ist-kein-Fehler-Gedanke wie
  // beim Thumbnail).
  //
  // `hatBild` fest `true`: dieses Overlay entsteht ausschliesslich ueber einen Klick auf das
  // Thumbnail (siehe `Artikelbild` oben, `if (!klickbar || fehlgeschlagen) return bild`) - der Klick
  // existiert also nur, wenn das Thumbnail NICHT `fehlgeschlagen` ist, `hatBild={false}` also nicht
  // griff. `bVorschauBild` (thumb) und `bBild` (card) sind zwar zwei verschiedene Spalten (siehe
  // Kommentar unten), aber beide haengen an DEMSELBEN `kBild`-Datensatz - `hatBild` beschreibt daher
  // beide gleichermassen, eine eigene Vorab-Auskunft fuer die Kartengroesse liefert kein Endpunkt.
  const { objectUrl: grossObjectUrl, meldeAnzeigefehler: grossMeldeAnzeigefehler } =
    useAuthentifiziertesBild(grossUrl, true, true);

  // Fokus beim Oeffnen in den Dialog - die Rueckgabe an das ausloesende Element liegt bei
  // `Artikelbild.schliessenUndFokusZurueck` (siehe Kopfkommentar), NICHT hier: diese Komponente
  // kennt das ausloesende Element nicht und soll es auch nicht kennen muessen.
  useLayoutEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // Escape auf `document`, sinngemaess wie `KontextMenue.tsx` (Eigenheit 9) - nicht als React-
  // `onKeyDown` auf dem Wurzelknoten: die Taste muss unabhaengig davon greifen, WELCHER Knoten
  // innerhalb des Dialogs gerade den Fokus traegt.
  //
  // NACHBESSERUNG (Pruefung 5bdfb6c, Punkt 9): `aria-modal="true"` (unten) erklaert diesen Dialog
  // als modal - ohne Fokusfalle wanderte Tab bislang HINTER ihn, ein gebrochenes Versprechen. Der
  // Dialog traegt genau EIN fokussierbares Element (den Schliessen-Knopf; das Bild selbst hat kein
  // `tabIndex`) - Tab/Shift+Tab halten den Fokus deshalb schlicht darauf, statt eine Ringliste fuer
  // einen einzigen Eintrag zu bauen.
  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === "Escape") {
        onSchliessen();
        return;
      }
      if (ereignis.key === "Tab") {
        ereignis.preventDefault();
        schliessenKnopfRef.current?.focus();
      }
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, [onSchliessen]);

  function beiHintergrundKlick(ereignis: ReactMouseEvent<HTMLDivElement>): void {
    if (ereignis.target === ereignis.currentTarget) onSchliessen();
  }

  return createPortal(
    // NACHBESSERUNG (Pruefung 5bdfb6c, Punkt 4): Hintergrund/Blur/Schatten kommen NICHT mehr aus
    // einer eigenen Nachbildung, sondern aus `.board-editor-overlay`/`.board-editor-dialog`
    // (`bestand.css:246-259`) - derselbe Weg wie `Users/PasswortDialog.tsx:66`. Die eigene
    // Nachbildung hatte die Deckkraft still von .48 auf .6 geaendert und stand als einzige
    // Neubau-Flaeche NICHT im Farbwaechter (`neubau-css.test.ts`) - beides entfaellt so.
    // `artikelbild-overlay` bleibt als ZUSAETZLICHE, farblose Klasse stehen (Selektor-Hook fuer
    // Tests/eigene Feinheiten wie den z-index unten), traegt selbst aber keine CSS-Regel mehr.
    //
    // z-index-Probe: `.board-editor-overlay` traegt 85 (bestand.css) - hoeher als `#drawer` (50)
    // und `#overlay` (40), also ueber jedem geoeffneten Drawer (`Positionen.tsx`,
    // `PositionsTabelle.tsx`). In der Anlagemaske (`PositionsEditor.tsx`, selbst schon
    // `.board-editor-overlay open`) gewinnt bei gleichem z-index der SPAETER an `document.body`
    // angehaengte Portal-Knoten ueber die Dokumentreihenfolge - Portale haengen dort grundsaetzlich
    // an, unabhaengig von der Verschachtelung im React-Baum.
    <div
      className="board-editor-overlay open artikelbild-overlay"
      role="presentation"
      onClick={beiHintergrundKlick}
    >
      <div
        ref={dialogRef}
        className="board-editor-dialog artikelbild-overlay-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={beschriftung}
        tabIndex={-1}
      >
        <button
          ref={schliessenKnopfRef}
          type="button"
          className="artikelbild-overlay-close"
          aria-label="Schließen"
          onClick={onSchliessen}
        >
          <Ikone name="x" />
        </button>
        {grossObjectUrl ? (
          // NACHBESSERUNG (Pruefung fc68056, Punkt 3): dasselbe Netz wie am Thumbnail -
          // `holeBinaerMitStatus` prueft nur den HTTP-Status, ein zwischengeschaltetes 200-mit-HTML
          // wuerde sonst wieder als Bruchbild-Symbol des Browsers enden.
          <img
            className="artikelbild-overlay-img"
            src={grossObjectUrl}
            alt={beschriftung}
            onError={grossMeldeAnzeigefehler}
          />
        ) : (
          <img className="artikelbild-overlay-img" src={artikelbildPlatzhalter()} alt="" aria-hidden="true" />
        )}
      </div>
    </div>,
    document.body,
  );
}
